import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { IMenuItemExtended, MenuItemType } from '@components/menu';
import { Policy, PolicyStatusText } from '@models/policy';
import {
  AllPolicyDialogTypes,
  EndorsementPolicyDialogType,
  PolicyDialogType,
  PolicyRelatedDestinations,
  PolicyRelatedSources,
  PolicyTransaction,
  PolicyTransactionStatus,
  RenewDialogType,
  PolicyTransactionTypes,
} from '@models/policyTransaction';
import { Sync, ExclamationCircleSolid } from '@assets/svgs';
import { DocumentStatus } from '@models/document';
import { PolicyGetResponse } from '@lio/api-types';
import { ReportingQuoteSourceLabels } from '@models/quotes';
import { PolicyRelationType, PolicyRenewalStatus, PolicyStatus } from '@lio/rule-engine';
import { ShortDateFormat, formatDateToTimezone, getDateFormatter } from '@utils/date';
import { DetailRow } from '@components/detailsPanel/DetailRow.type';
import { Box, Typography } from '@mui/material';
import { PolicyTerm } from '@models/policyTerm';
import { UserStoreSlice } from '@store/types/UserStoreSlice.type';
import { formatAddress } from '@utils/address';
import { formatBillingMethod } from '@utils/formatBillingMethods';
import { ProductOfferingId } from '@models/newQuote';
import { ClockFormat } from '@lio/rule-engine';
import { getContinuedToPolicy } from '@utils/policy/getContinuedToPolicy';
export type PolicyPanelType = 'Details';

export type PolicyDetailsRow = DetailRow & {
  field: DetailsFieldNames;
  key: (data: string) => string;
  valueGetter: (params: {
    policy: PolicyGetResponse;
    fieldName?: DetailsFieldNames;
    policyTransactions?: PolicyTransaction[];
    isInternalUser?: boolean;
    attributes: {
      billingMethod?: string;
    };
  }) => string | undefined | null | ReactNode;
  isVisible: (params: { isCustomerUser?: boolean; isInternalUser?: boolean }) => boolean;
};

export enum DetailsFieldNames {
  AccountManager = 'CSR / Account Manager',
  AccountNumber = 'Account Number',
  BillingAddress = 'Billing Address',
  BillingEmail = 'Email',
  BillingInformation = 'Billing Information',
  BillingPhone = 'Phone',
  BillingMethod = 'Billing Method',
  BillingPlan = 'Billing Plan',
  BillingStatus = 'BillingStatus',
  Brokerage = 'Brokerage',
  ProducerEmail = 'Producer Email',
  BusinessClass = 'Business Class',
  PackageLabel = 'Line Of Business',
  CO = 'C/O',
  CSR = 'LIO CSR',
  Email = 'Email',
  LioDetails = 'LIO Details',
  LossRatio = 'Loss Ratio',
  LossRatioGeneratedDate = 'Loss Ratio Generated Date',
  ComplianceReviewDate = 'Compliance Review Date',
  MailingAddress = 'Mailing Address',
  NamedInsured = 'Named Insured',
  Phone = 'Phone',
  PolicyNumber = 'Policy Number',
  PolicyPeriod = 'Active Term',
  Product = 'Product',
  Premium = 'Premium',
  PrimaryContact = 'Primary Contact',
  Producer = 'Producer',
  ProductLabel = 'Product Label',
  Source = 'Source',
  Status = 'Status',
  Underwriter = 'Underwriter',
  WritingCompany = 'Writing Company',
}

export type TPolicyDetailFunctionBody = {
  packageLabel?: string;
  /** The policy's renewal status */
  renewalStatus?: PolicyRenewalStatus;
  /** The policy status */
  status?: string;
  /** List of all policy transaction */
  policyTransactions: PolicyTransaction[];
  /** Information about the logged in user's roles */
  userAuth: UserStoreSlice['userAuth']; // { isJrOps: boolean; isLioSales: boolean; isAdmin: boolean; isPortalAdmin: boolean };
  doNotRenew: boolean;
  /** List of any related policies */
  relatedPolicySources?: PolicyRelatedSources[];
  /** List of any policies this policy has been continued into */
  relatedPolicyDestinations?: PolicyRelatedDestinations[];
  policyTerms: PolicyTerm[];
  billingMethod: string;
  offeringId?: string;
  /** Policy review at renewal information */
  hasActiveReviewAtRenewal?: boolean;
  hasReviewAtRenewalActivity?: boolean;
  /** Whether the policy has a pending BOR transfer request */
  hasPendingBORTransferRequest?: boolean;
};

export interface IPolicyDialogMenuItem extends IMenuItemExtended<TPolicyDetailFunctionBody> {
  showDialogType: AllPolicyDialogTypes | null;
}

const getCommonPolicyDetailMenuItems = ({
  handleCreateLossRunReport,
}: {
  handleCreateLossRunReport: () => void;
}): IPolicyDialogMenuItem[] => [
  {
    type: MenuItemType.Default,
    text: 'Generate Loss Runs',
    showDialogType: null,
    isShown: () => {
      // intentionally show for all policies regardless of status
      return true;
    },
    action: () => {
      handleCreateLossRunReport();
    },
  },
];

export const getBrokerPolicyDetailMenuItems = ({
  handleCreateLossRunReport,
}: {
  handleCreateLossRunReport: () => void;
}): IPolicyDialogMenuItem[] => [...getCommonPolicyDetailMenuItems({ handleCreateLossRunReport })];

const TERMINATED_STATUSES = new Set<PolicyStatus>([
  PolicyStatus.Canceled,
  PolicyStatus.PendingCancelation,
  PolicyStatus.Expired,
]);

const canEndorseTerminatedPolicy = (userAuth: TPolicyDetailFunctionBody['userAuth']) => {
  return userAuth.isUnderwriter || userAuth.isPortalAdmin || userAuth.isAdmin || userAuth.isSrOps || userAuth.isJrOps;
};

const isEndorsementStatusAllowed = (
  status: TPolicyDetailFunctionBody['status'],
  userAuth: TPolicyDetailFunctionBody['userAuth'],
) => {
  if (status === PolicyStatus.Active) {
    return true;
  }

  if (TERMINATED_STATUSES.has(status as PolicyStatus)) {
    return canEndorseTerminatedPolicy(userAuth);
  }

  return false;
};

export const getPolicyDetailsMenuItems = ({
  handleCreateLossRunReport,
}: {
  handleCreateLossRunReport: () => void;
}): IPolicyDialogMenuItem[] => [
  {
    type: MenuItemType.Default,
    text: 'Endorse Policy',
    showDialogType: EndorsementPolicyDialogType.Endorsement,
    isShown: ({ policyTransactions }) => {
      return !policyTransactions?.some(
        (t) => t.transactionType === PolicyTransactionTypes.Endorsement && t.status === PolicyTransactionStatus.Created,
      );
    },
    isDisabled: ({ status, policyTransactions, userAuth }) => {
      const hasBlockingCreatedTransaction = policyTransactions?.some(
        (el) => el.status === PolicyTransactionStatus.Created && el.transactionType !== PolicyTransactionTypes.Renewal,
      );
      const hasPendingRenewal = policyTransactions?.some(
        (el) => el.status === PolicyTransactionStatus.Created && el.transactionType === PolicyTransactionTypes.Renewal,
      );
      const canResolveOutOfSequence =
        userAuth.isAdmin || userAuth.isPortalAdmin || userAuth.isUnderwriter || userAuth.isSrOps;

      return (
        hasBlockingCreatedTransaction ||
        (hasPendingRenewal && !canResolveOutOfSequence) ||
        !isEndorsementStatusAllowed(status, userAuth)
      );
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Continue Endorsement',
    showDialogType: EndorsementPolicyDialogType.Endorsement,
    isShown: ({ policyTransactions }) => {
      return policyTransactions?.some(
        (t) => t.transactionType === PolicyTransactionTypes.Endorsement && t.status === PolicyTransactionStatus.Created,
      );
    },
    isDisabled: ({ status, userAuth }) => !isEndorsementStatusAllowed(status, userAuth),
  },
  {
    type: MenuItemType.Default,
    text: 'Update Bill Plan',
    showDialogType: EndorsementPolicyDialogType.UpdateBillPlan,
    isShown: ({ billingMethod }) => {
      return billingMethod !== 'AgencyBill';
    },
    isDisabled: ({ status, policyTransactions }) => {
      if (status !== PolicyStatus.Active) return true;

      return Boolean(policyTransactions?.find((t) => t.status === PolicyTransactionStatus.Created));
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Generate Conditional Renewal',
    showDialogType: RenewDialogType.ConditionalRenewal,
    isShown: ({ userAuth }) => userAuth.isInternalUser && !userAuth.isLioSales,
    isDisabled: ({ policyTransactions }) => {
      const createdRenewalExists = policyTransactions?.some(
        (t) => t.transactionType === PolicyTransactionTypes.Renewal && t.status === PolicyTransactionStatus.Created,
      );
      const existingConditionalRenewalTransaction = policyTransactions?.some(
        (t) =>
          t.transactionType === PolicyTransactionTypes.ConditionalRenewal &&
          (t.status === PolicyTransactionStatus.Created || t.status === PolicyTransactionStatus.Processed),
      );

      return !createdRenewalExists || !!existingConditionalRenewalTransaction;
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Mark for Nonrenewal',
    showDialogType: RenewDialogType.DoNotRenew,
    isShown: ({ doNotRenew }) => !doNotRenew,
  },
  {
    type: MenuItemType.Default,
    text: 'Rescind Nonrenewal',
    showDialogType: RenewDialogType.RescindDoNotRenew,
    isShown: ({ doNotRenew }) => doNotRenew,
  },
  {
    type: MenuItemType.Default,
    text: 'Mark for Review at Renewal',
    showDialogType: PolicyDialogType.MarkForReviewAtRenewal,
    isShown: ({ userAuth, hasActiveReviewAtRenewal }) => {
      return userAuth.isInternalUser && !hasActiveReviewAtRenewal;
    },
    isDisabled: () => false,
  },
  {
    type: MenuItemType.Default,
    text: 'Remove Review at Renewal',
    showDialogType: PolicyDialogType.RemoveReviewAtRenewal,
    isShown: ({ userAuth, hasActiveReviewAtRenewal }) => {
      return userAuth.isInternalUser && !!hasActiveReviewAtRenewal;
    },
    isDisabled: () => false,
  },
  {
    type: MenuItemType.Default,
    text: 'Create Policy Renewal',
    showDialogType: PolicyDialogType.Renew,
    isShown: ({ userAuth, policyTransactions }) => {
      const existingRenewalPolicyTransaction =
        policyTransactions?.length > 0
          ? policyTransactions.some(
              (el) =>
                el.status === PolicyTransactionStatus.Created && el.transactionType === PolicyTransactionTypes.Renewal,
            )
          : false;

      return !existingRenewalPolicyTransaction && userAuth.isInternalUser && !userAuth.isLioSales;
    },
    isDisabled: ({ renewalStatus, policyTransactions, relatedPolicyDestinations }) => {
      const existingPolicyTransaction = policyTransactions?.some((el) => el.status === PolicyTransactionStatus.Created);
      const hasBeenContinued = !!getContinuedToPolicy(relatedPolicyDestinations);

      return existingPolicyTransaction || renewalStatus !== PolicyRenewalStatus.Eligible || hasBeenContinued;
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Continue Policy Renewal',
    showDialogType: PolicyDialogType.Renew,
    isShown: ({ policyTransactions }) => {
      const existingRenewalPolicyTransaction = policyTransactions?.some(
        (el) => el.status === PolicyTransactionStatus.Created && el.transactionType === PolicyTransactionTypes.Renewal,
      );

      return existingRenewalPolicyTransaction || false;
    },
    isDisabled: ({ status }) => status !== PolicyStatus.Active,
  },
  {
    type: MenuItemType.Default,
    text: 'Cancel Policy',
    showDialogType: PolicyDialogType.Cancelation,
    // Expired policies can still be cancelled (e.g. to backdate a cancellation within the last term), internal users only
    isShown: ({ status, userAuth }) =>
      status === PolicyStatus.Active || (status === PolicyStatus.Expired && userAuth.isInternalUser),
    isDisabled: ({ userAuth, policyTransactions }) => {
      const existingRenewalPolicyTransaction = policyTransactions?.some(
        (el) => el.status === PolicyTransactionStatus.Created && el.transactionType === PolicyTransactionTypes.Renewal,
      );

      return existingRenewalPolicyTransaction || userAuth.isLioSales || userAuth.isJrOps;
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Rescind Cancellation',
    showDialogType: PolicyDialogType.Rescindment,
    isShown: ({ status }) => status === PolicyStatus.PendingCancelation,
  },
  {
    type: MenuItemType.Default,
    text: 'Reinstate Policy',
    showDialogType: PolicyDialogType.Reinstatement,
    isShown: ({ status }) => status === PolicyStatus.Canceled,
    isDisabled: ({ userAuth }) => userAuth.isLioSales || userAuth.isJrOps,
  },
  {
    type: MenuItemType.Default,
    text: 'Rewrite Policy',
    showDialogType: PolicyDialogType.Rewrite,
    isShown: ({ status, policyTransactions }) => {
      const existingPolicyTransaction = policyTransactions?.some(
        (t) =>
          t.transactionType === PolicyTransactionTypes.NewBusinessRewrite &&
          t.status === PolicyTransactionStatus.Created,
      );

      return !existingPolicyTransaction && status === PolicyStatus.Canceled;
    },
    isDisabled: ({ relatedPolicySources, userAuth }) => {
      const { isAdmin, isPortalAdmin, isLioSales, isUnderwriter } = userAuth;

      const policyContainsUnderlyingCoverage = !!relatedPolicySources?.find(
        (ps) => ps.policyRelationType === PolicyRelationType.UnderlyingCoverage,
      );

      return isLioSales || (policyContainsUnderlyingCoverage && !(isUnderwriter || isAdmin || isPortalAdmin));
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Continue Rewrite',
    showDialogType: PolicyDialogType.Rewrite,
    isShown: ({ status, policyTransactions }) => {
      const existingPolicyTransaction = policyTransactions?.some(
        (t) =>
          t.transactionType === PolicyTransactionTypes.NewBusinessRewrite &&
          t.status === PolicyTransactionStatus.Created,
      );

      return existingPolicyTransaction && status === PolicyStatus.Canceled;
    },
    isDisabled: ({ userAuth }) => userAuth.isLioSales,
  },
  {
    type: MenuItemType.Default,
    text: 'Manage Roles',
    showDialogType: PolicyDialogType.ManageRoles,
    isShown: ({ userAuth }) => {
      return userAuth.isInternalUser;
    },
    isDisabled: () => false,
  },
  {
    type: MenuItemType.Divider,
    showDialogType: PolicyDialogType.CreateLinkedPolicy,
    isShown: ({ userAuth, packageLabel }) => {
      return (
        userAuth.isInternalUser &&
        !userAuth.isLioSales &&
        (packageLabel === 'Package' || packageLabel === 'Primary General Liability')
      );
    },
  },
  {
    type: MenuItemType.Default,
    text: 'Create Linked Policy',
    showDialogType: PolicyDialogType.CreateLinkedPolicy,
    isShown: ({ userAuth, packageLabel }) => {
      return (
        userAuth.isInternalUser &&
        !userAuth.isLioSales &&
        (packageLabel === 'Package' || packageLabel === 'Primary General Liability')
      );
    },
    isDisabled: ({ status, offeringId }) => {
      const isSpecialEvents = offeringId === ProductOfferingId.SpecialEvent;
      return status !== PolicyStatus.Active || isSpecialEvents;
    },
  },
  {
    type: MenuItemType.Divider,
    showDialogType: null,
    isShown: ({ userAuth }) => {
      return userAuth.isInternalUser;
    },
  },
  ...getCommonPolicyDetailMenuItems({ handleCreateLossRunReport }),
  {
    type: MenuItemType.Default,
    text: 'View External Sent Emails',
    showDialogType: PolicyDialogType.ExternalEmails,
    isShown: ({ userAuth }) => {
      return userAuth.isInternalUser;
    },
    isDisabled: () => false,
  },
  //

  {
    type: MenuItemType.Divider,
    showDialogType: null,
    isShown: ({ userAuth, hasReviewAtRenewalActivity }) => {
      return userAuth.isInternalUser && hasReviewAtRenewalActivity;
    },
  },
  {
    type: MenuItemType.Default,
    text: 'See Review at Renewal Activity',
    showDialogType: PolicyDialogType.ReviewAtRenewalActivity,
    isShown: ({ userAuth, hasReviewAtRenewalActivity }) => {
      return userAuth.isInternalUser && hasReviewAtRenewalActivity;
    },
    isDisabled: () => false,
  },
  {
    type: MenuItemType.Default,
    text: 'View BOR Transfer Request',
    showDialogType: PolicyDialogType.ViewBORTransfer,
    isShown: ({ hasPendingBORTransferRequest, userAuth }) =>
      !!hasPendingBORTransferRequest && (userAuth.isAdmin || userAuth.isPortalAdmin),
    isDisabled: () => false,
  },
];

const getFormattedTermPremium = (value?: number | string | null) => {
  return value !== null && Number(value) > -1 ? `$${Number(value).toFixed(2)}` : '-';
};

const getPolicyDetailsSections = (
  clockFormat: ClockFormat,
): { title?: string; shownInitially?: boolean; rows: { fields: PolicyDetailsRow[] }[] }[] => [
  {
    // basic policy information
    shownInitially: true,
    rows: [
      {
        fields: [
          {
            field: DetailsFieldNames.NamedInsured,
            key: (policyId) => `${policyId}-NamedInsured`,
            width: '100%',
            valueGetter: ({ policy, isInternalUser }) => {
              return isInternalUser ? (
                <Link to={`/customer/${policy.customer.id}`} style={{ textDecoration: 'none' }}>
                  <Typography
                    variant='bodySmall'
                    sx={{
                      fontWeight: 'bold',
                      color: 'secondary.main',

                      ':hover': {
                        borderBottom: `1px solid white`,
                        borderColor: 'secondary.main',
                      },
                    }}
                  >
                    {policy.customer.name}
                  </Typography>
                </Link>
              ) : (
                policy?.customer?.name
              );
            },
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.MailingAddress,
            key: (policyId) => `${policyId}-MailingAddress`,
            width: '33%',
            valueGetter: ({
              policy,
            }: {
              policy: PolicyGetResponse;
              fieldName?: DetailsFieldNames;
              policyTransactions?: PolicyTransaction[];
              isInternalUser?: boolean;
            }) => {
              const mailingAddress = policy?.addresses.find((address) => address.type === 'Mailing');
              return mailingAddress ? formatAddress(mailingAddress) : '-';
            },
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.PrimaryContact,
            width: '33%',
            key: (policyId) => `${policyId}-careOf-PrimaryContact`,
            valueGetter: ({ policy, fieldName }) => {
              const mainAddress = policy?.addresses.find((address) => address.type === 'Mailing');
              const address =
                policy?.addresses.find(
                  (address) => address.type === (fieldName === DetailsFieldNames.PolicyNumber ? 'Mailing' : 'Billing'),
                ) ?? mainAddress;
              return address?.careOf ? address?.careOf : '-';
            },
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.Email,
            key: () => `PolicyMailEmail`,
            width: '33%',
            valueGetter: ({ policy }) => policy?.emails.find((email) => email.type === 'Main')?.email,
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.Product,
            key: (policyId) => `${policyId}-policyType-insurancePolicyName`,
            width: '33%',
            valueGetter: ({ policy }) => policy?.boundQuote?.offering.insuranceProduct.name,
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.ProductLabel,
            key: (policyId) => `${policyId}-ProductLabel-Name`,
            width: '33%',
            valueGetter: ({ policy }) => policy?.boundQuote?.productLabel?.name,
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.BusinessClass,
            key: (policyId) => `${policyId}-BusinessClass-Name`,
            width: '33%',
            valueGetter: ({ policy }) => policy?.boundQuote?.productOption?.name,
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.PackageLabel,
            width: '33%',
            key: (policyId) => `${policyId}-PackageLabel-Name`,
            valueGetter: ({ policy }) => (policy as any)?.packageLabel,
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.WritingCompany,
            key: (policyId) => `${policyId}-WritingCompamy-Name`,
            width: '33%',
            valueGetter: ({ policy }) => policy?.boundQuote?.writingCompany?.name,
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.PolicyPeriod,
            key: (policyId) => `${policyId}--policyPeriod`,
            width: '33%',
            valueGetter: ({ policy }) =>
              `${formatDateToTimezone(policy?.effectiveOn ?? new Date(), getDateFormatter(clockFormat, ShortDateFormat))} - ${formatDateToTimezone(policy?.expiresOn ?? new Date(), getDateFormatter(clockFormat, ShortDateFormat))}`,
            multiline: true,
            noBold: true,
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.Premium,
            key: (policyId) => `${policyId}-PolicyPremium`,
            width: '33%',
            valueGetter: ({ policy }) => {
              // TODO: Remove this adhoc type declaration and use 'policy.policyCost' after the schema is updated
              type PolicyWithCost = Policy & { policyCost: number };
              const policyWithCost = policy as unknown as PolicyWithCost;
              return getFormattedTermPremium(policyWithCost.policyCost);
            },
            multiline: true,
            noBold: true,
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.Status,
            key: (policyId) => `${policyId}-policyStatus`,
            width: '33%',
            valueGetter: ({ policy }) => (policy?.status ? PolicyStatusText[policy?.status] : '-'),
            multiline: true,
            noBold: true,
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.LossRatio,
            key: (policyId) => `${policyId}-lossRatio`,
            width: '33%',
            valueGetter: ({ policy }) => {
              if (policy?.lossRatio?.lossRatio === null || policy?.lossRatio.lossRatio === undefined) {
                return '-' as string | number;
              }
              return `${(policy.lossRatio.lossRatio * 100).toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}%`;
            },
            multiline: true,
            noBold: true,
            isVisible: ({ isCustomerUser }) => {
              return !isCustomerUser;
            },
          },
          {
            field: DetailsFieldNames.LossRatioGeneratedDate,
            key: (policyId) => `${policyId}-lossRatioGeneratedDate`,
            width: '33%',
            valueGetter: ({ policy }) =>
              policy?.lossRatio?.createdAt
                ? formatDateToTimezone(policy.lossRatio?.createdAt, getDateFormatter(clockFormat, ShortDateFormat))
                : ('-' as string | number),
            multiline: true,
            noBold: true,
            isVisible: ({ isCustomerUser }) => {
              return !isCustomerUser;
            },
          },
          {
            field: DetailsFieldNames.ComplianceReviewDate,
            key: (policyId) => `${policyId}-complianceReviewDate`,
            width: '33%',
            valueGetter: ({ policy }) =>
              policy?.complianceReviewDate
                ? formatDateToTimezone(policy.complianceReviewDate, getDateFormatter(clockFormat, ShortDateFormat))
                : ('-' as string | number),
            multiline: true,
            noBold: true,
            isVisible: ({ isInternalUser }) => {
              return !!isInternalUser;
            },
          },
          {
            field: DetailsFieldNames.Source,
            key: (policyId) => `${policyId}-quoteSource`,
            width: '33%',
            valueGetter: ({ policy }) =>
              policy?.reportingQuoteSource ? ReportingQuoteSourceLabels[policy.reportingQuoteSource] : '-',
            multiline: true,
            noBold: true,
            isVisible: ({ isInternalUser }) => {
              return !!isInternalUser;
            },
          },
        ],
      },
    ],
  },
  {
    // billing information
    title: 'Billing Information',
    shownInitially: false,
    rows: [
      {
        fields: [
          {
            field: DetailsFieldNames.CO,
            width: '100%',
            key: (policyId) => `${policyId}-PolicyCareOf`,
            valueGetter: ({ policy, fieldName }) => {
              const mainAddress = policy?.addresses.find((address) => address.type === 'Mailing');
              const address =
                policy?.addresses.find(
                  (address) => address.type === (fieldName === DetailsFieldNames.PolicyNumber ? 'Mailing' : 'Billing'),
                ) ?? mainAddress;
              return address?.careOf ? address?.careOf : '-';
            },
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.BillingAddress,
            key: (policyId) => `${policyId}-BillingAddress`,
            width: '33%',
            valueGetter: ({
              policy,
            }: {
              policy: PolicyGetResponse;
              fieldName?: DetailsFieldNames;
              policyTransactions?: PolicyTransaction[];
              isInternalUser?: boolean;
            }) => {
              const billingAddress = policy?.addresses.find((address) => address.type === 'Billing');
              return billingAddress ? formatAddress(billingAddress) : '-';
            },
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.BillingPhone,
            width: '33%',
            key: (policyId) => `${policyId}-BillingPhone`,
            valueGetter: ({ policy, fieldName }) => {
              const mainPhoneNumber = policy?.phoneNumbers.find((phone) => phone.type === 'Main');
              const phoneNumber =
                policy?.phoneNumbers.find(
                  (phone) => phone.type === (fieldName === DetailsFieldNames.PolicyNumber ? 'Mailing' : 'Billing'),
                ) ?? mainPhoneNumber;
              return phoneNumber?.number;
            },
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.BillingEmail,
            key: (policyId) => `${policyId}-BillingEmail`,
            width: '33%',
            valueGetter: ({ policy }) => {
              const mainEmail = policy?.emails.find((email) => email.type === 'Main');
              const billingEmail = policy?.emails.find((email: any) => email.type === 'Billing') ?? mainEmail;
              return billingEmail?.email;
            },
            isVisible: () => true,
          },
        ],
      },
      {
        fields: [
          {
            field: DetailsFieldNames.BillingMethod,
            width: '33%',
            key: (policyId) => `${policyId}-BillingMethod`,
            valueGetter: ({ attributes }) => {
              if (!attributes?.billingMethod) {
                return '-';
              }

              return formatBillingMethod(attributes.billingMethod);
            },
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.BillingPlan,
            width: '33%',
            key: (policyId) => `${policyId}-BillingPlan`,
            valueGetter: ({ policy }) => policy?.billingPlan?.label,
            isVisible: () => true,
          },
        ],
      },
    ],
  },
  {
    // Brokerage Details
    title: 'Brokerage Details',
    shownInitially: false,
    rows: [
      {
        fields: [
          {
            field: DetailsFieldNames.Brokerage,
            key: (policyId) => `${policyId}-Brokerage`,
            width: '33%',
            valueGetter: ({ policy }) => policy?.brokerage.name,
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.Producer,
            key: (policyId) => `${policyId}-Producer`,
            width: '66%',
            valueGetter: ({ policy }) =>
              policy?.boundQuote?.brokerageProducer.user
                ? `${policy?.brokerageProducer.user.firstName} ${policy?.brokerageProducer.user.lastName}`
                : '-',
            isVisible: () => true,
          },
          {
            field: DetailsFieldNames.ProducerEmail,
            key: (policyId) => `${policyId}-BrokerageDetails-email`,
            width: '100%',
            valueGetter: ({ policy }) => policy?.brokerageProducer.user?.email,
            isVisible: () => true,
          },
        ],
      },
    ],
  },
  {
    // LIO Details
    title: 'LIO Details',
    shownInitially: false,
    rows: [
      {
        fields: [
          {
            field: DetailsFieldNames.Underwriter,
            key: (policyId) => `${policyId}-Underwriter`,
            width: '100%',
            valueGetter: ({ policy }) =>
              policy?.boundQuote?.underwriter.user
                ? `${policy?.boundQuote?.underwriter.user.firstName} ${policy?.boundQuote?.underwriter.user.lastName}`
                : '-',
            isVisible: () => true,
          },
        ],
      },
    ],
  },
];

enum DocumentFieldNames {
  Document = 'document',
  FileName = 'fileName',
  Date = 'date',
  Summary = 'summary',
  Menu = 'menu',
}

const documentColumns = [
  {
    id: 1,
    field: DocumentFieldNames.Document,
    headerName: 'Document',
    width: 220,
  },
  {
    id: 2,
    field: DocumentFieldNames.FileName,
    headerName: 'Filename',
    width: 220,
  },
  {
    id: 3,
    field: DocumentFieldNames.Date,
    headerName: 'Date',
    width: 220,
  },
  {
    id: 4,
    field: DocumentFieldNames.Summary,
    headerName: 'Summary',
    width: 220,
  },
  {
    id: 5,
    field: DocumentFieldNames.Menu,
    headerName: '',
    width: 220,
  },
];

enum TransactionsFieldNames {
  Type = 'type',
  EffectiveDate = 'effectiveDate',
  ProcessedDate = 'processedDate',
  Change = 'change',
  PolicyCostAfter = 'policyCostAfter',
  Status = 'status',
  Delete = 'delete',
}

const transactionsColumns = [
  {
    field: TransactionsFieldNames.Type,
    headerName: 'Type',
    width: 270,
  },
  {
    field: TransactionsFieldNames.EffectiveDate,
    headerName: 'Effective Date',
    width: 180,
  },
  {
    field: TransactionsFieldNames.ProcessedDate,
    headerName: 'Processed Date',
    width: 260,
  },
  {
    field: TransactionsFieldNames.Change,
    headerName: 'Change',
    width: 220,
  },
  {
    field: TransactionsFieldNames.PolicyCostAfter,
    headerName: 'Policy Cost After',
    width: 220,
  },
  {
    field: TransactionsFieldNames.Status,
    headerName: 'Status',
    width: 180,
  },
  {
    field: TransactionsFieldNames.Delete,
    headerName: '',
    width: 80,
  },
];

enum PolicyTabs {
  Documents = 0,
  Subjectivities = 1,
  TransactionHistory = 2,
  AssociatedPolicies = 3,
  PolicyTerms = 4,
}

const IconTypes = {
  [DocumentStatus.Pending]: (
    <Box sx={{ color: 'primary.dark' }}>
      <Sync />
    </Box>
  ),
  [DocumentStatus.InProgress]: (
    <Box sx={{ color: 'warning.main' }}>
      <ExclamationCircleSolid />
    </Box>
  ),
  [DocumentStatus.Failed]: (
    <Box sx={{ color: 'error.main' }}>
      <ExclamationCircleSolid />
    </Box>
  ),
};

export {
  getPolicyDetailsSections,
  documentColumns,
  transactionsColumns,
  IconTypes,
  TransactionsFieldNames,
  DocumentFieldNames,
  PolicyTabs,
};
